import { Heading } from "@sentricmusic/ui";
import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// import { useUIStateStore } from "@/store/uiStateStore";
import useSiteConfigUnAuth from "@/api/sanity/queries/getSiteConfigWithDomain";
import { apiCall } from "@/api/shared";
import Alert from "@/components/Alert";
import AuthPrimaryButton from "@/components/AuthPrimaryButton";
import ButtonLink from "@/components/ButtonLink";
import CloneEnvironmentSelector from "@/components/CloneEnvironmentSelector";
import Input from "@/components/Input";
import Spacer from "@/components/Spacer";
import Loading from "@/layouts/Loading";
// import AppState from "@/store/appstate";
import useAppStore from "@/store/appStateZustand";

function Login() {
  const { data: domainConfig, isSuccess: domainConfigIsSuccess } = useSiteConfigUnAuth();
  // const isTunecore = useUIStateStore(state => state.isTunecore);

  // Check if this is ACTUALLY a TuneCore domain (not just forced mode for testing)
  const isActualTunecoreDomain = domainConfig?.display_name === "TuneCore";
  const [kycObj, setKycObj] = useState(null);
  const { t } = useTranslation();
  const locationPathname = useRouterState({ select: s => s.location.pathname });
  const [password1, setPassword1] = useState("");
  const [username, setUsername] = useState("");
  const [valid, setValid] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const usernameRegex = /^(?=[\w.-]{6,24}$)(?!.*[_.-]{2})[^_.-].*[^_.-]$/;
  const search = useSearch({ strict: false });
  const rawRedirect = search.redirect ?? "/";
  // Guard against protocol-relative paths (e.g. //evil.com) — only allow same-origin paths
  const redirectUrl = typeof rawRedirect === "string" && rawRedirect.startsWith("/") && !rawRedirect.startsWith("//")
    ? rawRedirect
    : "/";
  const navigate = useNavigate();
  // const accessToken = AppState.getRawState()?.authentication?.access_token;
  const accessToken = useAppStore(s => s.authentication?.access_token);
  const login = async () => {
    setPending(true);
    setError(null);
    // const apiUrl = AppState.getRawState()?.apiUrl;
    const apiUrl = useAppStore.getState().apiUrl;
    const details = await fetch(`${apiUrl}/rightsapp/login`, {
      method: "POST",
      body: JSON.stringify({
        username,
        password: password1,
      }),
    })
      .then(res => res.json())
      .catch((e) => {
        setError(`${e.name}: ${e.message}`);
      });

    if (details?.access_token) {
      // get artist accounts
      // Calculate token expiration time (assuming expires_in is in seconds)
      if (details.expires_in) {
        details.expires_at = Date.now() + (details.expires_in * 1000);
      }
      // AppState.update((s) => {
      //   s.authentication = details;
      // });
      useAppStore.setState({ authentication: details });
      return;
    }
    if (details?.error) {
      setError(details.error_description);
    }
    setPending(false);
  };

  const checkValid = (passed = {}) => {
    let val = true;

    // password check
    const pass = passed.password1 || password1;
    const validPassword = pass.trim().length > 6;
    if (!validPassword) {
      val = false;
    }

    // username check
    const user = passed.username || username;
    const validUsername = user.trim().length > 5;
    if (!validUsername) {
      val = false;
    }

    // user regex check
    if (!usernameRegex.test(user)) {
      val = false;
      setError(t("regex:login-username-regex-error"));
    }

    setValid(val);
  };

  useEffect(() => {
    const fetchKycStatus = async () => {
      const kyc = await apiCall({
        endpoint: "/rightsapp/get",
        path: "/api/v1/user/getkyc",
      });
      // AppState.update((s) => {
      //   s.kyc = kyc;
      // });
      useAppStore.setState({ kyc });
      setKycObj(kyc);
    };
    if (accessToken) {
      fetchKycStatus();
    }
  }, [accessToken]);

  useEffect(() => {
    if (kycObj) {
      const showSecurity = kycObj.kycrequired
        && !kycObj.kycpassed
        && kycObj.messages && kycObj.messages.length > 0 && kycObj.messages[0] === "KYC Required";
      const showSecurityStep2 = kycObj.kycrequired
        && !kycObj.kycpassed
        && kycObj.messages && kycObj.messages.length > 0 && kycObj.messages[0] === "KYC still pending";
      if (showSecurity) {
        navigate({ to: "/security" });
      }
      else if (showSecurityStep2) {
        navigate({ to: "/security/begin-security-check" });
      }
      else {
        navigate({ to: redirectUrl, replace: true });
      }
    }
  }, [kycObj, navigate, redirectUrl]);

  // handler onKeypress enter key
  useEffect(() => {
    const keyDownHandler = (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        document.getElementById("btnLogin").click();
      }
    };

    document.addEventListener("keydown", keyDownHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, []);

  useEffect(() => {
    if (isActualTunecoreDomain && locationPathname === "/login" && domainConfigIsSuccess) {
      window.location.href = "https://www.tunecore.com/";
    }
  }, [isActualTunecoreDomain, locationPathname, domainConfigIsSuccess]);

  if (domainConfigIsSuccess && !isActualTunecoreDomain) {
    return (
      <div className="container mx-auto">
        <div className="flex flex-col items-center justify-center p-4 text-center min-h-screen-minus-footer">
          <Heading as="h1" className="text-white">{t("auth:login-header")}</Heading>
          <div className="w-full lg:w-1/3 text-ink-primary">
            <CloneEnvironmentSelector />
            <Spacer />
            <Input
              placeholder={t("auth:username-placeholder")}
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                checkValid({ username: e.target.value });
                setError(t("regex:login-username-regex-error"));
              }}
              id="txtUsername"
              whiteLabelText
              className="bg-transparent auth-page-input"
            />
            <Spacer />
            <Input
              type="password"
              placeholder={t("auth:password-placeholder")}
              value={password1}
              onChange={(e) => {
                setPassword1(e.target.value);
                checkValid({ password1: e.target.value });
                setError(null);
              }}
              id="txtPassword"
              whiteLabelText
              className="bg-transparent auth-page-input "
            />
            <Spacer />
            <AuthPrimaryButton
              arrow
              id="btnLogin"
              disabled={!valid}
              pending={pending}
              onClick={() => {
                login();
              }}
            >
              {t("auth:login-button")}
            </AuthPrimaryButton>
            {error && (
              <>
                <Spacer />
                <Alert msg={error} />
              </>
            )}
          </div>
          <Spacer />
          <div className="flex">
            <ButtonLink
              className="mr-8"
              id="btnSignUp"
              onClick={() => navigate({ to: "/sign-up" })}
            >
              {t("common:sign-up-link")}
            </ButtonLink>
            <ButtonLink
              id="btnForgot"
              onClick={() => navigate({ to: "/reset-password" })}
            >
              {t("auth:forgot-password")}
            </ButtonLink>
          </div>
        </div>
      </div>
    );
  }
  return <Loading />;
}

export default Login;
